import { useEffect, useRef, useState } from 'react';
import { useAtlasStore } from '../../../store/atlasStore';

export const useSparkProjectData = (projectFqn: string | undefined) => {
  const store = useAtlasStore();
  const [firstFetchCompleted, setFirstFetchCompleted] = useState(false);
  const firstFetchCompletedRef = useRef<boolean>(false);
  const [isBackgroundRefreshing, setIsBackgroundRefreshing] = useState(false);
  // Worker status now comes from global store (provider updates it)
  const workerIsOnline = useAtlasStore(s => s.workerIsOnline);
  
  // Function to fetch project data and handle loading states
  const fetchProjectData = async () => {
    if (!projectFqn) return;
    
    try {
      // If first fetch is already completed, we're doing a background refresh
      if (firstFetchCompleted) {
        setIsBackgroundRefreshing(true);
      }
      
      await store.fetchSparkProjectDetails(projectFqn);
      
      // Also ensure we have creative types
      if (store.sparkCreativeTypes.length === 0) {
        await store.fetchSparkCreativeTypes();
      }
      
      if (!firstFetchCompleted) {
        // After initial fetch, get the current total completed job count from the endpoint
        // Note: We don't use project._completed_job_count because that's unseen jobs, not total
        setFirstFetchCompleted(true);
        firstFetchCompletedRef.current = true;
      }
    } catch (error) {
      console.error("Error fetching project data:", error);
    } finally {
      setIsBackgroundRefreshing(false);
    }
  };
  
  // Set up initial data fetch
  // Intentional: run on projectFqn change only; internal functions reference current refs/state
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (projectFqn) {
      // Do initial full data fetch
      fetchProjectData();
    }
    
    // Clean up on unmount
    return () => {
    };
  }, [projectFqn]);
  /* eslint-enable react-hooks/exhaustive-deps */
  
  // Calculate initial loading state - during first load but not background refreshes
  const isInitialLoading = store.isLoadingSpark.projectDetails && !firstFetchCompleted;
  
  return {
    project: store.currentSparkProject,
    flow: store.currentSparkFlow,
    creatives: store.currentSparkCreatives,
    jobs: store.currentSparkJobs,
    creativeTypes: store.sparkCreativeTypes,
    isLoading: store.isLoadingSpark.projectDetails || !firstFetchCompleted,
    isInitialLoading,
    isBackgroundRefreshing,
    error: store.sparkError,
    refreshData: fetchProjectData,
    // Expose current worker online status
    workerIsOnline,
  };
};

export default useSparkProjectData; 
